/* eslint-disable @typescript-eslint/no-unused-vars */
import { useMutation } from '@apollo/client';
import {
  NotificationTypeEnum,
  PushNotificationFn,
  useNotification,
} from '@prismamedia/one-components';
import { generatePath, useHistory } from 'react-router-dom';
import { paths } from '../../../routing/Router/paths';
import {
  GetCardQuery_query_card_Card_categories_CardCategory,
  GetCardQuery_query_card_Card_tags_CardTag,
  UpsertCardMutation,
  UpsertCardMutationVariables,
} from '../../../__generated__/queries-web';
import {
  getCreateForResource,
  mapDeleteRelationship,
} from '../../ArticleEdit/utils/articleMutationVariables';
import { UPSERT_CARD } from '../../../apollo/mutations/card.web.graphql';
import { CardState } from './types';

export enum SaveCardActions {
  CREATE,
  UPDATE,
  DELETE,
}

const showSaveCardError = (
  error: any,
  pushNotification: PushNotificationFn,
) => {
  console.error('[SAVE CARD ERROR] ', error);

  pushNotification({
    message: "Erreur lors de l'enregistrement",
    type: NotificationTypeEnum.error,
  });
};

export const useSaveCard = () => {
  const history = useHistory();
  const { pushNotification } = useNotification();
  const [upsert] = useMutation<UpsertCardMutation, UpsertCardMutationVariables>(
    UPSERT_CARD,
  );

  const saveCard = async (
    card: CardState,
    deprecatedCategories: GetCardQuery_query_card_Card_categories_CardCategory[],
    deprecatedTags: GetCardQuery_query_card_Card_tags_CardTag[],
    actionType: SaveCardActions,
  ) => {
    const {
      id,
      brandKey,
      secondaryTitle,
      description,
      ...updateCardData
    } = card;

    const serializedCard = {
      ...card,
      secondaryTitle: secondaryTitle?.length ? secondaryTitle : null,
      description: description?.length ? description : null,
      body: card.body || null,
      media: card.media ? card.media : null,
      categories: {
        create: getCreateForResource(card.categories, 'category'),
      },
      tags: {
        create: getCreateForResource(card.tags, 'tag'),
      },
    };

    const variables = {
      where: { id: serializedCard.id },
      create: { ...serializedCard, brandKey },
      update: {
        ...(actionType === SaveCardActions.UPDATE && {
          ...updateCardData,
          secondaryTitle: secondaryTitle?.length ? secondaryTitle : null,
          description: description?.length ? description : null,
          body: card.body || null,
          categories: {
            ...serializedCard.categories,
            delete: mapDeleteRelationship(deprecatedCategories),
          },
          tags: {
            ...serializedCard.tags,
            delete: mapDeleteRelationship(deprecatedTags),
          },
        }),
      },
    };

    try {
      const result = await upsert({ variables });

      if (!result?.data?.upsertCard.id || !result.data?.upsertCard.status) {
        showSaveCardError(result, pushNotification);
        return;
      }

      const {
        data: {
          upsertCard: { status },
        },
      } = result;

      pushNotification({
        message: 'Encart enregistré',
        type: NotificationTypeEnum.success,
      });

      history.push({
        pathname: generatePath(paths.CARD_EDIT, { brandKey, id }),
        search: `status=${status}`,
      });
    } catch (e) {
      showSaveCardError(e, pushNotification);
    }
  };

  return { saveCard };
};
/* eslint-enable @typescript-eslint/no-unused-vars */
